/**
 * MinigameOverlay - Vollständiges Minispiel-Overlay
 *
 * Wird über dem aktuellen Spielinhalt angezeigt, wenn ein Minispiel aktiv ist.
 * Verwaltet den kompletten Minispiel-Lifecycle:
 * 1. Splash (Ankündigung)
 * 2. Modal (Spielen)
 * 3. Ranking (Ergebnisse)
 *
 * Nutzt useSyncExternalStore für sauberes State-Management ohne globale Variablen.
 */
import React, { useCallback, useSyncExternalStore, useEffect, useRef } from 'react';
import socketService from '../../socketService';
import musicService from '../../musicService';
import { MINIGAME_EVENTS } from '../../shared/core/minigameConstants';
import {
  subscribe,
  getSnapshot,
  getServerSnapshot,
  setupSocketListeners,
  startPlaying,
} from '../../state/minigameStore';
import MinigameSplash from './MinigameSplash';
import MinigameModal from './MinigameModal';
import MinigameRanking from './MinigameRanking';

export default function MinigameOverlay() {
  // Subscribe to minigame store using React 18's useSyncExternalStore
  const { phase, minigameData, rankings, playerIndex } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );

  // Setup socket listeners on mount
  useEffect(() => {
    setupSocketListeners();
    // Note: We don't cleanup on unmount because listeners should persist
    // across the app lifecycle. Use cleanupSocketListeners() for testing.
  }, []);

  // Track previous phase to detect transitions
  const prevPhaseRef = useRef(null);

  // Handle music changes based on minigame phase
  useEffect(() => {
    const prevPhase = prevPhaseRef.current;
    prevPhaseRef.current = phase;

    // Minigame started (splash appears) → switch to minigame music
    if (phase === 'splash' && prevPhase === null) {
      musicService.playTemporary('minigame');
    }

    // Minigame ended (phase becomes null) → resume previous music
    if (phase === null && prevPhase !== null) {
      musicService.resumePrevious();
    }
  }, [phase]);

  // Callback when splash completes
  const handleSplashComplete = useCallback(() => {
    startPlaying();
  }, []);

  // Callback when game completes
  const handleGameComplete = useCallback(() => {
    // Server sends results, we wait for that
  }, []);

  // Callback when ranking is acknowledged
  const handleRankingContinue = useCallback(() => {
    socketService.emit(MINIGAME_EVENTS.ACKNOWLEDGE);
  }, []);

  // Nichts rendern wenn kein Minispiel aktiv
  if (!phase) {
    return null;
  }

  return (
    <>
      {/* Phase 1: Splash/Ankündigung */}
      {phase === 'splash' && minigameData && (
        <MinigameSplash
          minigameId={minigameData.minigameId}
          name={minigameData.name}
          description={minigameData.description}
          icon={minigameData.icon}
          triggeredBy={minigameData.triggeredBy}
          onComplete={handleSplashComplete}
        />
      )}

      {/* Phase 2: Minispiel */}
      {phase === 'playing' && minigameData && (
        <MinigameModal
          minigameId={minigameData.minigameId}
          config={minigameData.config}
          playerIndex={playerIndex}
          initialState={minigameData.globalState || {}}
          onComplete={handleGameComplete}
        />
      )}

      {/* Phase 3: Ergebnisse/Ranking */}
      {phase === 'ranking' && rankings && (
        <MinigameRanking
          minigameId={minigameData?.minigameId}
          rankings={rankings}
          myPlayerIndex={playerIndex}
          onContinue={handleRankingContinue}
        />
      )}
    </>
  );
}
